import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faDownload,
  faFloppyDisk,
  faCircleCheck,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES } from '@/constants';
import { Button, Spinner } from '@/components/ui';
import { PresentationEditor } from '@/components/PresentationEditor/PresentationEditor';
import { PresentationChat } from '@/components/PresentationChat/PresentationChat';
import { useDriveUpload, useOnlyOfficeForcesave } from '@/api/usePresentationWizard';
import { migrateV1toV2, isDeckV2 } from '@/lib/migrateSlideSchema';
import type { PresentationItem } from '@/types/presentation';
import type { DeckV2 } from '@/types/slide';
import styles from './PresentationEdit.module.css';

type LayoutContext = {
  setHeaderSlot: (node: ReactNode) => void;
  setHeaderTitle: (node: ReactNode) => void;
};

export function PresentationEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderSlot, setHeaderTitle } = useOutletContext<LayoutContext>();
  const driveUpload = useDriveUpload();
  const forcesave = useOnlyOfficeForcesave();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [, setChangedSlides] = useState<number[]>([]);
  const changedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: item,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['presentation-detail', id],
    queryFn: async () => {
      const res = await api.get<PresentationItem>(`${API_ROUTES.presentations}${id}/`);
      return res.data;
    },
    enabled: Boolean(id),
  });

  const detailPath = ROUTES.dashboardPresentationDetail.replace(':id', String(id));

  const deckV2 = useMemo(() => {
    if (!item?.deck_json) return null;
    const raw =
      typeof item.deck_json === 'string'
        ? JSON.parse(item.deck_json as unknown as string)
        : item.deck_json;
    return isDeckV2(raw) ? raw : migrateV1toV2(raw);
  }, [item?.deck_json]);

  // Clamp current slide when deck changes
  useEffect(() => {
    if (deckV2 && currentSlide >= deckV2.slides.length) {
      setCurrentSlide(Math.max(0, deckV2.slides.length - 1));
    }
  }, [deckV2, currentSlide]);

  const handleDeckUpdate = useCallback(
    (newDeck: DeckV2, affected?: number[]) => {
      queryClient.setQueryData(
        ['presentation-detail', id],
        (old: PresentationItem | undefined) => {
          if (!old) return old;
          return { ...old, deck_json: newDeck };
        },
      );

      // Highlight changed slides briefly
      if (affected && affected.length > 0) {
        setChangedSlides(affected);
        if (changedTimerRef.current) clearTimeout(changedTimerRef.current);
        changedTimerRef.current = setTimeout(() => setChangedSlides([]), 2000);
      }
    },
    [id, queryClient],
  );

  // Navigate slides via keyboard (only when not typing)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      if (!deckV2) return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCurrentSlide((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCurrentSlide((prev) => Math.min(deckV2.slides.length - 1, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deckV2]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (changedTimerRef.current) clearTimeout(changedTimerRef.current);
    };
  }, []);

  // ── Action handlers for the injected header slot ──
  const handleDownloadPptx = useCallback(() => {
    const url = item?.pptx_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('No PPTX file available');
    }
  }, [item?.pptx_url]);

  const googleLoginForSlides = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      if (!id) return;
      toast.loading('Uploading to Google Drive...', { id: 'drive-editor' });
      driveUpload.mutate(
        { presentationId: id, access_token: tokenResponse.access_token },
        {
          onSuccess: (res) => {
            window.open(res.drive_url, '_blank');
            toast.success('Uploaded to Google Drive', { id: 'drive-editor' });
          },
          onError: () => {
            toast.error('Failed to upload to Google Drive', { id: 'drive-editor' });
          },
        },
      );
    },
    onError: () => {
      toast.error('Google sign-in was cancelled');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const handleOpenInGoogleSlides = useCallback(() => {
    if (item?.my_drive_url) {
      window.open(item.my_drive_url, '_blank');
      return;
    }
    googleLoginForSlides();
  }, [item?.my_drive_url, googleLoginForSlides]);

  const handleSave = useCallback(() => {
    if (!id) return;
    toast.loading('Saving…', { id: 'pres-save' });
    forcesave.mutate(id, {
      onSuccess: () => {
        toast.success('Saved', { id: 'pres-save' });
        queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
        queryClient.invalidateQueries({ queryKey: ['onlyoffice-config', id] });
      },
      onError: () => {
        toast.error('Could not save right now', { id: 'pres-save' });
      },
    });
  }, [id, forcesave, queryClient]);

  // ── Inject the unified header into DashboardLayout ──
  const slideCount = deckV2?.slides.length ?? 0;
  const isDriveUploading = driveUpload.isPending;
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!item) {
      setHeaderTitle(null);
      setHeaderSlot(null);
      return;
    }
    setHeaderTitle(
      <span className={styles.headerTitleText}>
        <span className={styles.headerTitleName}>{item.name}</span>
        {slideCount > 0 && (
          <span className={styles.headerSlideCounter}>
            · Slide {currentSlide + 1} / {slideCount}
          </span>
        )}
      </span>,
    );
    setHeaderSlot(
      <>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={() => navigate(detailPath)}
          title="Back to details"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span className={styles.headerBtnLabel}>Back</span>
        </button>
        <button
          type="button"
          className={`${styles.headerBtn} ${styles.headerBtnPrimary}`}
          onClick={handleSave}
          disabled={forcesave.isPending}
          title="Save changes"
        >
          <FontAwesomeIcon
            icon={
              forcesave.isPending
                ? faSpinner
                : forcesave.isSuccess
                  ? faCircleCheck
                  : faFloppyDisk
            }
            spin={forcesave.isPending}
          />
          <span className={styles.headerBtnLabel}>
            {forcesave.isPending ? 'Saving…' : forcesave.isSuccess ? 'Saved' : 'Save'}
          </span>
        </button>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={handleDownloadPptx}
          title="Download PPTX"
        >
          <FontAwesomeIcon icon={faDownload} />
          <span className={styles.headerBtnLabel}>Download</span>
        </button>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={handleOpenInGoogleSlides}
          disabled={isDriveUploading}
          title="Open in Google Slides"
        >
          <FontAwesomeIcon icon={faGoogle} />
          <span className={styles.headerBtnLabel}>
            {isDriveUploading ? 'Uploading…' : 'Google Slides'}
          </span>
        </button>
      </>,
    );
    return () => {
      setHeaderTitle(null);
      setHeaderSlot(null);
    };
  }, [
    item?.name,
    item?.my_drive_url,
    item?.pptx_url,
    currentSlide,
    slideCount,
    isDriveUploading,
    detailPath,
    forcesave.isPending,
    forcesave.isSuccess,
    handleSave,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (isLoading) {
    return (
      <div className={styles.centeredState}>
        <Spinner />
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div className={styles.centeredState}>
        <p className={styles.errorText}>Could not load the presentation.</p>
        <Button variant="secondary" onClick={() => navigate(detailPath)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </Button>
      </div>
    );
  }

  if (!item.pptx_url) {
    return (
      <div className={styles.centeredState}>
        <p className={styles.errorText}>No PPTX file available.</p>
        <Button variant="secondary" onClick={() => navigate(detailPath)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </Button>
      </div>
    );
  }

  const currentSlideData = deckV2?.slides[currentSlide] ?? null;

  return (
    <div className={styles.page}>
      {/* Editor area: ONLYOFFICE + chat */}
      <div className={styles.editorArea}>
        <div className={styles.editorMain}>
          <PresentationEditor presentation={item} />
        </div>

        <div className={styles.chatPanel}>
          {id && deckV2 && (
            <PresentationChat
              presentationId={id}
              currentSlideIndex={currentSlide}
              currentSlide={currentSlideData}
              onDeckUpdate={handleDeckUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
}
